import React, { useState, useEffect, useCallback } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api, { getApiErrorMessage } from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { useExpenditureLabels } from '../../../contexts/ExpenditureLabelsContext';
import { AddressLookup } from '../../../components/AddressLookup';
import {
  Plus, Trash2, Save, Search, X, Edit, MapPin, Users, Package, Scale
} from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../../../components/SortableTableHeader';

const METHOD_OPTIONS = [
  { value: 'fifo', label: 'FIFO', desc: 'First In, First Out' },
  { value: 'lifo', label: 'LIFO', desc: 'Last In, First Out' },
  { value: 'wac', label: 'WAC', desc: 'Weighted Average Cost' },
];

export function InventoryManagementPanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locSort, handleLocSort] = useSortState('code', 'asc');
  const [locSearch, setLocSearch] = useState('');
  const [editLoc, setEditLoc] = useState(null); // Location being edited
  const [allUsers, setAllUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newOverride, setNewOverride] = useState({ category_code: '', method: 'fifo' });
  const { labels: expLabels } = useExpenditureLabels();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sRes, lRes, uRes, cRes] = await Promise.all([
        api.get('/api/admin/inventory-settings').catch(() => ({ data: { settings: getDefaults() } })),
        api.get('/api/inventory/locations').catch(() => ({ data: { data: [] } })),
        api.get('/api/admin/users?limit=200').catch(() => ({ data: { users: [] } })),
        api.get('/api/data/categories?page_size=200').catch(() => ({ data: { data: [] } })),
      ]);
      setSettings(sRes.data.settings || sRes.data || getDefaults());
      setLocations(lRes.data.data || lRes.data.locations || []);
      setAllUsers(uRes.data.users || []);
      setCategories(cRes.data.data || []);
    } catch { setSettings(getDefaults()); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const getDefaults = () => ({
    expenditure_code: 'nominal_code', mandatory: true, auto_create_order: false,
    inventory_method: 'fifo', expiry_date_consumable: false, category_valuation_overrides: [],
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/inventory-settings', settings);
      toast.success('Inventory settings saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const update = (field, value) => setSettings(p => ({ ...p, [field]: value }));

  const openNewLocation = () => {
    setEditLoc({
      location_code: '', description: '', location_type: 'warehouse',
      address: '', city: '', state: '', postal_code: '', country: '',
      inventory_managers: [], active: true, _isNew: true,
    });
  };

  const openEditLocation = (loc) => {
    setEditLoc({ ...loc, _isNew: false });
  };

  const saveLocation = async () => {
    if (!editLoc.location_code) { toast.error('Location Code is required'); return; }
    setSaving(true);
    try {
      if (editLoc._isNew) {
        await api.post('/api/inventory/locations', editLoc);
        toast.success('Location created');
      } else {
        await api.put(`/api/inventory/locations/${editLoc.location_code}`, editLoc);
        toast.success('Location updated');
      }
      setEditLoc(null);
      const r = await api.get('/api/inventory/locations');
      setLocations(r.data.data || []);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const deleteLocation = async () => {
    if (!editLoc?.location_code) return;
    if (!window.confirm(`Delete location ${editLoc.location_code}?`)) return;
    try {
      await api.delete(`/api/inventory/locations/${editLoc.location_code}`);
      toast.success('Location deleted');
      setEditLoc(null);
      const r = await api.get('/api/inventory/locations');
      setLocations(r.data.data || []);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Delete failed')); }
  };

  const toggleManager = (userId) => {
    setEditLoc(prev => {
      const managers = prev.inventory_managers || [];
      if (managers.includes(userId)) {
        return { ...prev, inventory_managers: managers.filter(m => m !== userId) };
      }
      return { ...prev, inventory_managers: [...managers, userId] };
    });
  };

  const filteredLocs = locSearch
    ? locations.filter(l => l.location_code?.toLowerCase().includes(locSearch.toLowerCase()) || l.description?.toLowerCase().includes(locSearch.toLowerCase()))
    : locations;

  if (loading) return <LoadingSpinner />;

  const expenditureOptions = [
    { value: 'exp_code_1', label: expLabels.exp_code_1 || 'Account Code 1' },
    { value: 'exp_code_2', label: expLabels.exp_code_2 || 'Account Code 2' },
    { value: 'exp_code_3', label: expLabels.exp_code_3 || 'Account Code 3' },
    { value: 'exp_code_4', label: expLabels.exp_code_4 || 'Account Code 4' },
    { value: 'exp_code_5', label: expLabels.exp_code_5 || 'Account Code 5' },
    { value: 'exp_code_6', label: expLabels.exp_code_6 || 'Account Code 6' },
    { value: 'cost_centre', label: expLabels.cost_centre || 'Cost Centre' },
  ];

  return (
    <div className="max-w-4xl" data-testid="inventory-management-panel">
      <h1 className="text-2xl font-bold mb-1">Inventory Management</h1>
      <p className="text-sm text-muted-foreground mb-6">Configure inventory settings and manage stock locations.</p>

      {/* Section 1: Inventory Control Settings */}
      <div className="border rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">1</span>
          Inventory Control
        </h3>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Label className="w-56 text-sm">Expenditure Codes</Label>
            <select value={settings?.expenditure_code || 'nominal_code'} onChange={e => update('expenditure_code', e.target.value)}
              className="w-56 h-9 rounded-md border border-input bg-background px-3 text-sm">
              {expenditureOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-4">
            <Label className="w-56 text-sm">Mandatory</Label>
            <Switch checked={settings?.mandatory} onCheckedChange={v => update('mandatory', v)} />
            <span className="text-xs text-muted-foreground">{settings?.mandatory ? 'Yes' : 'No'}</span>
          </div>
          <div className="flex items-center gap-4">
            <Label className="w-56 text-sm">Auto Create Replenishment Orders</Label>
            <Switch checked={settings?.auto_create_order} onCheckedChange={v => update('auto_create_order', v)} />
            <span className="text-xs text-muted-foreground">{settings?.auto_create_order ? 'Yes — auto-creates when reorder level reached' : 'No'}</span>
          </div>
          <div className="flex items-center gap-4">
            <Label className="w-56 text-sm">Default Valuation Method</Label>
            {METHOD_OPTIONS.map(m => (
              <label key={m.value} className="flex items-center gap-2 cursor-pointer" title={m.desc}>
                <input type="radio" name="inv_method" value={m.value} checked={settings?.inventory_method === m.value}
                  onChange={e => update('inventory_method', e.target.value)} className="h-4 w-4" />
                <span className="text-sm">{m.label}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <Label className="w-56 text-sm">Track Expiry Dates on Consumables</Label>
            <Switch checked={settings?.expiry_date_consumable} onCheckedChange={v => update('expiry_date_consumable', v)} />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={handleSave} disabled={saving}><Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save Settings'}</Button>
        </div>
      </div>

      {/* Section 2: Category Valuation Overrides */}
      <div className="border rounded-lg p-6 mb-6" data-testid="category-valuation-overrides">
        <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">2</span>
          Category Valuation Overrides
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          Override the default valuation method ({METHOD_OPTIONS.find(m => m.value === settings?.inventory_method)?.label || 'FIFO'}) for specific item categories.
          Serialised items always use Specific Identification regardless of category.
        </p>

        {(settings?.category_valuation_overrides || []).length > 0 && (
          <div className="border rounded-lg overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr>
                <th className="text-left px-3 py-2 font-semibold">Category</th>
                <th className="text-left px-3 py-2 font-semibold">Valuation Method</th>
                <th className="w-10" />
              </tr></thead>
              <tbody>
                {(settings?.category_valuation_overrides || []).map((ov, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-2 font-medium">{ov.category_code}</td>
                    <td className="px-3 py-2">
                      <select
                        value={ov.method}
                        onChange={e => {
                          const overrides = [...(settings.category_valuation_overrides || [])];
                          overrides[i] = { ...overrides[i], method: e.target.value };
                          update('category_valuation_overrides', overrides);
                        }}
                        className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                        data-testid={`override-method-${i}`}
                      >
                        {METHOD_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label} — {m.desc}</option>)}
                      </select>
                    </td>
                    <td className="px-2 text-center">
                      <button
                        onClick={() => {
                          const overrides = (settings.category_valuation_overrides || []).filter((_, idx) => idx !== i);
                          update('category_valuation_overrides', overrides);
                        }}
                        className="text-destructive hover:text-red-700 p-1"
                        data-testid={`remove-override-${i}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center gap-2">
          <select
            value={newOverride.category_code}
            onChange={e => setNewOverride(p => ({ ...p, category_code: e.target.value }))}
            className="h-8 w-56 rounded-md border border-input bg-background px-2 text-sm"
            data-testid="new-override-category"
          >
            <option value="">Select Category...</option>
            {categories
              .filter(c => !(settings?.category_valuation_overrides || []).some(ov => ov.category_code === c.code))
              .map(c => <option key={c.code} value={c.code}>{c.code}{c.description ? ` — ${c.description}` : ''}</option>)}
          </select>
          <select
            value={newOverride.method}
            onChange={e => setNewOverride(p => ({ ...p, method: e.target.value }))}
            className="h-8 w-40 rounded-md border border-input bg-background px-2 text-sm"
            data-testid="new-override-method"
          >
            {METHOD_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <Button
            size="sm"
            disabled={!newOverride.category_code}
            onClick={() => {
              if (!newOverride.category_code) return;
              const overrides = [...(settings.category_valuation_overrides || []), { ...newOverride }];
              update('category_valuation_overrides', overrides);
              setNewOverride({ category_code: '', method: 'fifo' });
            }}
            data-testid="add-override-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Override
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">Changes take effect after clicking "Save Settings" above.</p>
      </div>

      {/* Section 3: Location Master */}
      <div className="border rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">3</span>
          Inventory Locations
        </h3>
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
            <Input value={locSearch} onChange={e => setLocSearch(e.target.value)} placeholder="Search locations..." className="pl-8 h-8 text-sm" />
          </div>
          <span className="text-xs text-muted-foreground">{filteredLocs.length} location(s)</span>
          <div className="flex-1" />
          <Button size="sm" onClick={openNewLocation} data-testid="add-location-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add New Location
          </Button>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr>
              <th className="text-left px-3 py-2 font-semibold">Code</th>
              <th className="text-left px-3 py-2 font-semibold">Description</th>
              <th className="text-left px-3 py-2 font-semibold">Type</th>
              <th className="text-left px-3 py-2 font-semibold">Address</th>
              <th className="text-center px-3 py-2 font-semibold">Active</th>
              <th className="w-10"></th>
            </tr></thead>
            <tbody>
              {filteredLocs.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground text-xs">No locations found</td></tr>
              ) : sortData(filteredLocs, locSort.field, locSort.dir).map(l => (
                <tr key={l.location_code} className="border-t hover:bg-accent/30 cursor-pointer" onClick={() => openEditLocation(l)}>
                  <td className="px-3 py-2 font-semibold text-primary">{l.location_code}</td>
                  <td className="px-3 py-2">{l.description}</td>
                  <td className="px-3 py-2"><Badge className="text-[9px]">{l.location_type || 'warehouse'}</Badge></td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{[l.address, l.city, l.country].filter(Boolean).join(', ') || '—'}</td>
                  <td className="px-3 py-2 text-center">{l.active !== false ? <Badge className="text-[9px] bg-green-100 text-green-700">Yes</Badge> : <Badge className="text-[9px] bg-gray-100">No</Badge>}</td>
                  <td className="px-2"><Edit className="h-3.5 w-3.5 text-muted-foreground" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Location Edit Dialog — matches screenshot layout */}
      {editLoc && (
        <Dialog open onOpenChange={() => setEditLoc(null)}>
          <DialogContent className="max-w-2xl" data-testid="location-edit-dialog">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle>{editLoc._isNew ? 'Add Location' : 'Location — Edit'}</DialogTitle>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Active</span>
                  <Switch checked={editLoc.active !== false} onCheckedChange={v => setEditLoc(p => ({ ...p, active: v }))} />
                  <Badge className={`text-[9px] ${editLoc.active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100'}`}>
                    {editLoc.active !== false ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </div>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-6">
              {/* Left column */}
              <div className="space-y-4">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-primary">Location Code *</Label>
                  <Input value={editLoc.location_code} onChange={e => setEditLoc(p => ({ ...p, location_code: e.target.value.toUpperCase() }))}
                    readOnly={!editLoc._isNew} className={`h-8 text-sm ${!editLoc._isNew ? 'bg-muted' : ''}`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Location Description</Label>
                  <Textarea value={editLoc.description || ''} onChange={e => setEditLoc(p => ({ ...p, description: e.target.value }))}
                    rows={3} className="text-sm" maxLength={200} />
                  <p className="text-[10px] text-muted-foreground text-right">characters remaining {200 - (editLoc.description?.length || 0)}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Location Type</Label>
                  <select value={editLoc.location_type || 'warehouse'} onChange={e => setEditLoc(p => ({ ...p, location_type: e.target.value }))}
                    className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
                    <option value="warehouse">Warehouse</option>
                    <option value="drop_ship">Drop Ship</option>
                    <option value="supplier_held">Supplier Held</option>
                    <option value="mobile">Mobile / Truck</option>
                    <option value="office">Office</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Inventory Manager</Label>
                  <div className="border rounded-lg p-2 max-h-32 overflow-y-auto">
                    {allUsers.filter(u => u.active !== false).map(u => (
                      <label key={u.user_id} className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-accent/20 px-1 rounded text-xs">
                        <input type="checkbox" checked={(editLoc.inventory_managers || []).includes(u.user_id)}
                          onChange={() => toggleManager(u.user_id)} className="h-3.5 w-3.5" />
                        {u.first_name} {u.last_name}
                      </label>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    <Users className="h-3 w-3 inline mr-0.5" /> ({(editLoc.inventory_managers || []).length} / {allUsers.filter(u => u.active !== false).length})
                  </p>
                </div>
              </div>

              {/* Right column — Address */}
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Address Lookup</Label>
                  <AddressLookup
                    onSelect={addr => setEditLoc(p => ({
                      ...p,
                      address: addr.address || p.address,
                      city: addr.city || p.city,
                      state: addr.state_province || p.state,
                      postal_code: addr.zip_postal || p.postal_code,
                      country: addr.country || p.country,
                    }))}
                    placeholder="Search address..."
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Address</Label>
                  <Textarea value={editLoc.address || ''} onChange={e => setEditLoc(p => ({ ...p, address: e.target.value }))}
                    rows={2} className="text-sm" maxLength={200} />
                  <p className="text-[10px] text-muted-foreground text-right">characters remaining {200 - (editLoc.address?.length || 0)}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">City</Label>
                  <Input value={editLoc.city || ''} onChange={e => setEditLoc(p => ({ ...p, city: e.target.value }))} className="h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">State/Province</Label>
                  <Input value={editLoc.state || ''} onChange={e => setEditLoc(p => ({ ...p, state: e.target.value }))} className="h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Zip/Postal Code</Label>
                  <Input value={editLoc.postal_code || ''} onChange={e => setEditLoc(p => ({ ...p, postal_code: e.target.value }))} className="h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Country/Region</Label>
                  <Input value={editLoc.country || ''} onChange={e => setEditLoc(p => ({ ...p, country: e.target.value }))} className="h-8 text-sm" />
                </div>
              </div>
            </div>

            <div className="flex justify-between mt-4 pt-3 border-t">
              <div>
                {!editLoc._isNew && (
                  <Button variant="destructive" size="sm" onClick={deleteLocation}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={saveLocation} disabled={saving}>
                  <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="outline" onClick={() => setEditLoc(null)}>
                  <X className="h-3.5 w-3.5 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
